import { useState, useCallback, useEffect, useRef } from 'react';
import { useLoading } from '../contexts/LoadingContext';
import { useDbConnection } from '../hooks/useDbConnection';
import { useAuth } from '../hooks/useAuth';
import { useDatabase } from '../contexts/DatabaseContext';

export const useDatabaseConnectionViewModel = () => {
  // Try to load connection details and tablesData from localStorage
  const localConnectionDetails = (() => {
    try {
      const stored = localStorage.getItem('snowflake_connection_details');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const localTablesData = (() => {
    try {
      const stored = localStorage.getItem('snowflake_tables_data');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const localDatasourceId = localStorage.getItem('snowflake_datasource_id');

  const [connectionState, setConnectionState] = useState({
    isConnected: !!(localConnectionDetails && localTablesData),
    connectionDetails: localConnectionDetails || {
      account: '',
      username: '',
      password: '',
      warehouse: '',
      role: '',
      schema: '',
      database: '',
    },
    error: null,
    loading: false,
    tablesData: localTablesData || null,
    datasourceId: localDatasourceId || null,
  });

  const { showLoading, hideLoading } = useLoading();
  const { createSnowflakeConnection, loading: connectionLoading } = useDbConnection();
  const { user } = useAuth();
  const userId = user?.userDetails?.user_id;
  const { updateDatabaseState, clearDatabaseState } = useDatabase();

  const connect = useCallback(async (connectionDetails) => {
    showLoading();
    try {
      setConnectionState(prev => ({ ...prev, loading: true, error: null }));

      const result = await createSnowflakeConnection({
        userId: userId,
        ...connectionDetails,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to connect to database');
      }

      const { tablesData, datasourceId, status } = result.data;

      // Parse tablesData if it's a string
      let parsedTablesData = tablesData;
      if (typeof tablesData === 'string') {
        try {
          parsedTablesData = JSON.parse(tablesData);
        } catch (e) {
          console.error('Error parsing tablesData:', e);
          throw new Error('Failed to parse database structure');
        }
      }

      // Save connection details and tablesData to localStorage
      localStorage.setItem('snowflake_connection_details', JSON.stringify(connectionDetails));
      localStorage.setItem('snowflake_tables_data', JSON.stringify(parsedTablesData));
      localStorage.setItem('snowflake_datasource_id', datasourceId);

      // Extract tables and columns from the parsed data
      const tables = parsedTablesData.map(db => ({
        name: db.name,
        id: db.id,
        children: db.children.map(table => ({
          name: table.name,
          id: table.id,
          type: table.type,
          columns: table.children.map(column => ({
            name: column.name,
            id: column.id,
            type: column.type,
            dataType: column.textDatatype,
            tableName: column.tableName
          }))
        }))
      }));

      setConnectionState(prev => ({
        ...prev,
        isConnected: true,
        connectionDetails,
        loading: false,
        tablesData: parsedTablesData,
        datasourceId: datasourceId,
      }));

      updateDatabaseState({
        isConnected: true,
        datasourceId: datasourceId,
        tables,
        tablesData: parsedTablesData,
      });

      return result.data;
    } catch (error) {
      setConnectionState(prev => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
      throw error;
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, createSnowflakeConnection, userId, updateDatabaseState]);

  const disconnect = useCallback(() => {
    setConnectionState({
      isConnected: false,
      connectionDetails: {
        account: '',
        username: '',
        password: '',
        warehouse: '',
        role: '',
        schema: '',
        database: '',
      },
      error: null,
      loading: false,
      tablesData: null,
      datasourceId: null,
    });
    // Clear localStorage
    localStorage.removeItem('snowflake_connection_details');
    localStorage.removeItem('snowflake_tables_data');
    localStorage.removeItem('snowflake_datasource_id');
    clearDatabaseState();
  }, [clearDatabaseState]);

  const updateConnectionDetails = useCallback((field, value) => {
    setConnectionState(prev => {
      const updatedDetails = {
        ...prev.connectionDetails,
        [field]: value,
      };
      // Persist to localStorage
      localStorage.setItem('snowflake_connection_details', JSON.stringify(updatedDetails));
      return {
        ...prev,
        connectionDetails: updatedDetails,
      };
    });
  }, []);

  const handleError = useCallback((error) => {
    setConnectionState(prev => ({
      ...prev,
      error: error.message || 'An error occurred',
    }));
  }, []);

  // On mount, if localStorage has tablesData, update global database state
  const lastTablesDataRef = useRef();
  useEffect(() => {
    if (
      connectionState.isConnected &&
      connectionState.tablesData &&
      lastTablesDataRef.current !== connectionState.tablesData
    ) {
      updateDatabaseState({
        isConnected: true,
        tablesData: connectionState.tablesData,
        datasourceId: connectionState.datasourceId,
      });
      lastTablesDataRef.current = connectionState.tablesData;
    }
  }, [connectionState.isConnected, connectionState.tablesData, connectionState.datasourceId, updateDatabaseState]);

  return {
    connectionState: {
      ...connectionState,
      loading: connectionState.loading || connectionLoading,
    },
    connect,
    disconnect,
    updateConnectionDetails,
    onError: handleError,
  };
};

export default useDatabaseConnectionViewModel; 